<template>
  <div :class="[classes]">
    <div class="bg-gradient-to-b from-[#2DA7D7]/25 h-36 w-[100%] absolute left-0 top-0 pointer-events-none" />
    <div class="text-3xl font-medium relative z-10 flex space-x-2">
      <Settings2 class="mr-2 w-10 h-10" color="#000000" /> {{ $t("Add New Companies") }}
    </div>
    <div class="relative z-10 text-common-grey text-sm ml-12 -mt-2">
      {{ $t("Add records manually, upload a CSV file, or connect with your CRM") }}
    </div>
    <div class="relative flex w-full h-full gap-2 mt-8">
      <div class="w-2/5 cursor-pointer" @click="handleCsvClick">
        <div class="flex justify-center w-full h-full pointer-events-none">
          <div
            class="min-h-[230px] text-center justify-center max-w-4xl w-full border border-dashed rounded-md border-light-divider-blue dark:border-dark-divider-border">
            <div v-if="!(current_file || sheet_link_ready)" class="relative flex flex-col items-center h-full justify-center">
              <input class="custom-file-input" accept=".xlsx,.xls,.csv,.numbers" type="file" @change="setFile">
              <CloudUpload class="mx-auto pt-2 my-1/2 mt-[15px] mb-[28px] w-12 h-12" color="#899399" />
              <span class="mx-auto font-bold dark:text-dark-body-text">{{ $t("Drag & drop spreadsheet") }}</span>
              <span class="mx-auto text-sm dark:text-dark-search-bar-text">{{ $t("CSV and XLSX supported") }}</span>

              <button class="w-24 mx-auto my-8 primary-outline-btn">{{ $t("Upload file") }}</button>
            </div>
            <div v-else class="flex flex-col items-center justify-center w-full h-full">
              <CloudUpload class="mx-auto pt-2 my-1/2 mt-[25px] mb-[28px] w-12 h-12" color="#899399" />
              <span class="mx-auto dark:text-dark-body-text">{{ current_file?.name }}</span>
              <div class="flex flex-row w-[160px] mx-auto">
                <button class="w-12 p-2 mx-auto my-8 rounded-md dark:text-dark-body-text bg-red-error hover:bg-red-500" @click="resetFile">
                  X
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="w-3/5 flex flex-col">
        <span class="font-semibold">{{ $t("Manually add records") }}</span>
        <div class="flex items-center mt-4">
          <TextInput
            :value="url"
            :label="$t('URL')"
            :classes="'w-full'"
            input-classes="appearance-none cursor-move block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
            name="url"
            type="text"
            :placeholder="$t('e.g. google.com')"
            @click.stop
            @update:modelValue="
              ($event) => {
                url = $event
              }
            " />
          <div
            class="flex cursor-pointer items-center border-transparent bg-gradient-to-r from-[#2DA7D7] to-[#841FF4] ml-2 px-6 font-bold text-white rounded-full w-fit-content h-[40px] content-center text-[14px]"
            @click="addUrl()">
            <Plus class="text-white w-4 h-4 mr-2" aria-hidden="true" />
            <span>{{ $t("Add") }}</span>
          </div>
        </div>

        <div v-for="record in added_records" :key="record" class="border rounded-md mt-2 p-2 flex w-full justify-between">
          <div>
            {{ record }}
          </div>
          <Trash2
            class="w-5 h-5 transition-all text-common-grey ease-in-out cursor-pointer hover:text-red-500"
            @click="added_records = added_records.filter((r) => r !== record)" />
        </div>

        <span v-if="!is_free_plan" class="font-semibold mt-4">{{ $t("Connect with your CRM") }}</span>
        <IntegrationCard
          v-if="available_services.length && !is_free_plan"
          :onboarding="true"
          :all-services="available_services"
          :service="available_services.find((s) => s.reference == 'salesforce')" />

        <IntegrationCard
          v-if="available_services.length && !is_free_plan"
          class="mt-2"
          :onboarding="true"
          :all-services="available_services"
          :service="available_services.find((s) => s.reference == 'hubspot')" />
      </div>
    </div>

    <div class="flex gap-2 p-2 mt-7 justify-evenly">
      <button type="button" class="w-1/2 cancel-btn" @click="handleCancel">{{ $t("Cancel") }}</button>
      <button type="submit" class="w-1/2 primary-btn disabled:opacity-50" @click="handleSubmit">{{ $t("Finish") }}</button>
    </div>
  </div>
</template>

<script>
import { CloudUpload, Plus, Trash2, Settings2 } from "lucide-vue-next"
import IntegrationService from "@/services/integration.service"
import dataService from "@/services/data.service"
import IntegrationCard from "@/views/dashboard/admin/integrations/IntegrationCard.vue"
// eslint-disable-next-line vue/no-unused-components
import TextInput from "@/components/shared/TextInput.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    Trash2,
    IntegrationCard,
    Plus,
    TextInput,
    CloudUpload,
    // eslint-disable-next-line vue/no-unused-components
    Settings2
  },

  inject: ["setPageLayout", "updateLayouts"],
  props: {
    classes: {
      type: String,
      default: ""
    }
  },

  emits: ["cancel", "done"],

  data() {
    return {
      available_services: [],
      current_file: null,
      sheet_link_ready: false,
      url: null,
      is_free_plan: true,
      added_records: []
    }
  },

  created() {
    this.getIntegratedServices()
    this.getSubscriptions()
  },

  methods: {
    getSubscriptions() {
      dataService.mySubscriptions().then((res) => {
        const plan_items = res.data.data[0].items.data
        const first_item_plan_id = plan_items[0]?.plan?.product
        const second_item_plan_id = plan_items[1]?.plan?.product
        if (first_item_plan_id == "prod_QXsxG5HAXPwKFz" || second_item_plan_id == "prod_QXsxG5HAXPwKFz") {
          this.is_free_plan = true
        } else {
          this.is_free_plan = false
        }
      })
    },
    getIntegratedServices() {
      IntegrationService.allServices().then(({ data }) => {
        this.available_services = data
      })
    },

    handleCsvClick() {
      this.$router.push("/admin/integrations/spreadsheet/spreadsheet/upload")
      this.$emit("cancel")
    },
    handleSubmit() {
      if (this.added_records.length) {
        this.added_records.forEach((record, index) => {
          dataService.addRecord(record.replace("www.", "")).then(() => {
            if (index == this.added_records.length - 1) {
              this.$emit("done")
              this.$emitter.emit(ALL_EMITTERS.REFRESH_TABLE)
            }
          })
        })
      } else {
        this.$emit("done")
        this.$emitter.emit(ALL_EMITTERS.REFRESH_TABLE)
      }
    },
    handleCancel() {
      this.$emit("cancel")
    },
    setFile(event) {
      this.current_file = event.target.files[0]
    },
    resetFile() {
      this.current_file = ""
    },
    addUrl() {
      this.added_records.push(this.url)
      this.url = ""
    }
  }
}
</script>
